/**
 * VariantModal — RHF + Zod modal pour créer/éditer une variante d'œuvre.
 *
 * Direction C « fiche conservateur » : 5 blocs numérotés
 *   01 Type d'édition (4 cartes radio : Original / Édition limitée /
 *      Open edition / Set numérique). Le mode `digital` est une synthèse
 *      UI — il force `edition_kind='open'` + `medium_kind='digital'` au
 *      submit.
 *   02 Format (grille de cartes RadioCard, mono-select). Masqué en open
 *      (la spec POD porte les tailles) et le filtre kind dépend du mode.
 *   03 Support & encadrement (chip-toggles single-select). Visible
 *      uniquement pour `limited`/`original` (tirage atelier physique).
 *      Masqué en open (la spec Gelato porte la matière) et en digital
 *      (fichier sans papier/cadre).
 *   04 Tarif & disponibilité (Prix EUR avec suffix € / Stock / Spec POD
 *      / Nom commercial / État). Le champ Stock est gaté au tirage atelier
 *      physique — open = print-on-demand par construction (Gelato),
 *      digital = fichier infiniment dupliqué.
 *   05 Expédition (collapsible). Visible uniquement pour `limited`/
 *      `original` — open = Gelato packagise depuis son lab, digital =
 *      pas de colis.
 *
 * Mode `create` : ajoute une nouvelle row work_variants + insère sa
 *                 junction `work_variant_formats` (1:N depuis S2.5d).
 * Mode `edit`   : PATCH partiel (model_dump exclude_unset côté BE).
 *                 Si `format_ids` change, BE régénère les deliverables.
 *
 * Empty state catalogue : si `formats` vide, affiche un EmptyState pointant
 * vers `/settings`.
 *
 * Le swatch de l'encadrement, le label du support et les formats inclus
 * sont rendus dans la table parente (`VariantsPanel`) à partir du payload
 * `WorkVariantWithCatalogResponse` retourné par le BE après mutation.
 */

import { useMemo, useState } from 'react'
import { Link } from 'react-router'
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Circle, CircleDot, Disc, LayoutGrid } from 'lucide-react'
import { toast } from 'sonner'

import Button from '@/components/ui/Button'
import EmptyState from '@/components/ui/EmptyState'
import Field from '@/components/ui/Field'
import Input from '@/components/ui/Input'
import Modal from '@/components/ui/Modal'
import RadioCard from '@/components/ui/RadioCard'
import { FormField } from '@/components/forms/FormField'
import FormSection from '@/components/forms/FormSection'
import { useAtelierSettings } from '@/hooks/useAtelierSettings'
import { useFormats, useFramings, useSupports } from '@/hooks/useCatalog'
import { usePodPricingConfig } from '@/hooks/usePodCatalog'
import { useAddVariant, useDigitalFormatsPreview, useUpdateVariant } from '@/hooks/useWorks'
import { variantFormSchema, type VariantFormParsed, type VariantFormValues } from '@/schemas/works'
import { EDITION_KIND_FR, type WorkVariantWithCatalogResponse } from '@/types/works'

import {
  computePrefillCandidate,
  prefillSourceLabel,
  type PrefillSource,
} from './variantPricePrefill'

import './VariantModal.css'

interface VariantModalProps {
  workId: number
  /**
   * Ratio width/height de l'image source (S2.5e). Filtre les formats
   * compatibles si AU MOINS UN ratio source matche dans `formatMatchingTolerance`
   * (multi-source, migration 015). Vide tant qu'aucun master n'a été
   * uploadé → pas de filtre ratio (toutes les dimensions catalogue
   * restent sélectionnables).
   */
  workSourceRatios: number[]
  /** Tolérance ±5 % source de vérité = `media_service.FORMAT_MATCH_TOLERANCE`,
   *  exposée via `WorkResponse.format_matching_tolerance` (codegen). */
  formatMatchingTolerance: number
  mode: 'create' | 'edit'
  initial?: WorkVariantWithCatalogResponse
  onClose: () => void
  /** Si défini (mode `create` uniquement), affiche « ← Changer de type »
   *  dans le footer pour revenir au picker mono vs bulk. */
  onBack?: () => void
}

/** Mode d'édition UI — `digital` est une synthèse (= edition_kind='open' + formats kind='digital'). */
type EditionMode = 'original' | 'limited' | 'open' | 'digital'

function pickMatchingSourceRatio(
  format: { width_cm: number | string; height_cm: number | string },
  workSourceRatios: number[],
  tolerance: number,
): number | null {
  const w = Number(format.width_cm)
  const h = Number(format.height_cm)
  if (!Number.isFinite(w) || !Number.isFinite(h) || h <= 0) return null
  const formatRatio = w / h
  // Matching orientation-agnostique aligné avec
  // `media_service.pick_source_for_format` côté BE — un master portrait
  // 2:3 sert pour un format paysage 3:2 via flip côté pipeline.
  let best: number | null = null
  let bestDelta = Infinity
  for (const r of workSourceRatios) {
    if (!Number.isFinite(r) || r <= 0) continue
    const delta = Math.min(
      Math.abs(r - formatRatio) / formatRatio,
      Math.abs(1 / r - formatRatio) / formatRatio,
    )
    if (delta <= tolerance && delta < bestDelta) {
      best = r
      bestDelta = delta
    }
  }
  return best
}

function isFormatCompatibleWithWork(
  format: { width_cm: number | string; height_cm: number | string },
  workSourceRatios: number[],
  tolerance: number,
): boolean {
  if (workSourceRatios.length === 0) return true // Pas de master encore — pas de filtre.
  return pickMatchingSourceRatio(format, workSourceRatios, tolerance) !== null
}

const EDITION_MODE_CARDS: {
  value: EditionMode
  label: string
  description: string
  icon: typeof Circle
}[] = [
  { value: 'original', label: 'Original', description: 'Pièce unique', icon: Disc },
  { value: 'limited', label: 'Édition limitée', description: 'Tirages numérotés', icon: CircleDot },
  {
    value: 'open',
    label: 'Open edition',
    description: 'Multi-tailles via preset POD',
    icon: Circle,
  },
  { value: 'digital', label: 'Set numérique', description: 'Fichiers livrés', icon: LayoutGrid },
]

export default function VariantModal({
  workId,
  workSourceRatios,
  formatMatchingTolerance,
  mode,
  initial,
  onClose,
  onBack,
}: VariantModalProps) {
  const formatsQuery = useFormats({ only_active: true })
  const framingsQuery = useFramings({ only_active: true })
  const supportsQuery = useSupports({ only_active: true })

  const addMutation = useAddVariant()
  const updateMutation = useUpdateVariant()

  const formats = useMemo(() => formatsQuery.data ?? [], [formatsQuery.data])
  const framings = useMemo(() => framingsQuery.data ?? [], [framingsQuery.data])
  const supports = useMemo(() => supportsQuery.data ?? [], [supportsQuery.data])

  const isLoading = formatsQuery.isLoading || framingsQuery.isLoading || supportsQuery.isLoading

  // Détection mode initial : refonte POD v6.1 — `medium_kind` est la
  // politique unique de routage canal. `medium_kind='digital'` → mode UI
  // 'digital'. Sinon on retombe sur `edition_kind` (open/limited/original).
  const initialMode: EditionMode = useMemo(() => {
    if (!initial) return 'open'
    if (initial.medium_kind === 'digital') return 'digital'
    return initial.edition_kind ?? 'open'
  }, [initial])

  const [editionMode, setEditionMode] = useState<EditionMode>(initialMode)
  // Re-sync mode quand le catalogue arrive (en édition, formats est vide au
  // 1er render — `initialMode` change de 'open' à la vraie valeur dérivée
  // une fois `formats` peuplé). Pattern « adjust state when prop changes »
  // (React 19) — pas de useEffect.
  const [prevInitialMode, setPrevInitialMode] = useState<EditionMode>(initialMode)
  if (prevInitialMode !== initialMode) {
    setPrevInitialMode(initialMode)
    setEditionMode(initialMode)
  }

  const [showShipping, setShowShipping] = useState(true)

  // useForm doit être appelé inconditionnellement (rules-of-hooks). Defaults
  // ne dépendent pas du catalogue, donc safe à instancier même quand vide.
  //
  // Refonte POD v6.1 : `medium_kind` est dérivé de l'UI `editionMode` au
  // submit (digital ⇒ 'digital' ; sinon 'print'). On stocke 'print' par
  // défaut pour les nouvelles variantes — `original` est une variante
  // d'edition_kind, pas un medium_kind (cf. CHECK DB `chk_wv_digital_no_phys`).
  const defaultValues: VariantFormValues = {
    medium_kind: initial?.medium_kind === 'digital' ? 'digital' : 'print',
    format_id: initial?.format_id ? String(initial.format_id) : '',
    work_media_id: initial?.work_media_id ? String(initial.work_media_id) : '',
    printable_spec_id: initial?.printable_spec_id ? String(initial.printable_spec_id) : '',
    framing_id: initial?.framing_id ? String(initial.framing_id) : '',
    support_id: initial?.support_id ? String(initial.support_id) : '',
    edition_kind: initial?.edition_kind ?? 'open',
    edition_size: initial?.edition_size ?? undefined,
    price_cents: initial?.price_cents ?? 0,
    label_override: initial?.label_override ?? '',
    weight_g: initial?.weight_g ?? undefined,
    packaged_width_cm: initial?.packaged_width_cm ?? undefined,
    packaged_height_cm: initial?.packaged_height_cm ?? undefined,
    packaged_depth_cm: initial?.packaged_depth_cm ?? undefined,
    fragile: initial?.fragile ?? true,
    stock_qty: initial?.stock_qty ?? undefined,
    status: initial?.status ?? 'draft',
  }

  const form = useForm<VariantFormValues, unknown, VariantFormParsed>({
    resolver: zodResolver(variantFormSchema),
    defaultValues,
  })

  // S2.5d polish — preview live des livrables qui seront générés.
  // Doit être placé AVANT l'early return "Catalogue vide" pour respecter
  // rules-of-hooks. `useWatch` (vs `form.watch`) → compatible React
  // Compiler memoization.
  //
  // Refonte POD v6.1 : 1 variante = 1 format publiable (`format_id`
  // singulier). Le wizard bulk N→N variantes vit dans la PR-C suivante
  // (`<VariantsBulkCreateForm>`).
  const watchedFormatId = useWatch({ control: form.control, name: 'format_id' }) ?? ''
  const watchedStatus = useWatch({ control: form.control, name: 'status' }) ?? 'draft'
  const watchedFramingId = useWatch({ control: form.control, name: 'framing_id' }) ?? ''
  const watchedSupportId = useWatch({ control: form.control, name: 'support_id' }) ?? ''

  // ── Prefill silencieux du champ Prix (sticky) ────────────────────────
  //
  // Contrat :
  // - Le prefill est ONE-SHOT par session du modal : dès qu'une valeur
  //   > 0 est posée (par prefill silencieux OU par édition utilisatrice),
  //   le bloc « adjust state in render » ne touche plus jamais le champ.
  //   Pour relancer, fermer puis rouvrir le modal.
  // - shouldDirty: false évite que le prefill silencieux n'apparaisse
  //   comme une saisie utilisatrice dans le formState.
  //
  // Refonte tarifs 2026-05-15 — cascade simplifiée à 1 source :
  //   isDigital && atelier.digital_default_price_cents > 0
  //     → pose le NET atelier en prefill silencieux.
  // Le mode POD ('open') n'a plus de prefill (champ Prix caché — le BE
  // dérive du base_cost Gelato au publish). La matrice triple est
  // supprimée (migration 012).
  const [prefillSource, setPrefillSource] = useState<PrefillSource | null>(null)
  const atelierSettingsQuery = useAtelierSettings()
  const podPricingConfigQuery = usePodPricingConfig()

  const prefillCandidate = useMemo(
    () =>
      computePrefillCandidate({
        mode,
        isDigital: editionMode === 'digital',
        formatIds: watchedFormatId ? [String(watchedFormatId)] : [],
        framingId: String(watchedFramingId ?? ''),
        supportId: String(watchedSupportId ?? ''),
        atelierConfig: atelierSettingsQuery.data?.config ?? null,
        podPreview: null,
        podPricing: podPricingConfigQuery.data ?? null,
        formats,
        framings,
        supports,
      }),
    [
      editionMode,
      mode,
      watchedFormatId,
      watchedFramingId,
      watchedSupportId,
      atelierSettingsQuery.data,
      podPricingConfigQuery.data,
      formats,
      framings,
      supports,
    ],
  )

  // Pattern « adjust state in render » (cf. prevInitialMode plus haut).
  // Sticky : une fois qu'un prefill a été posé, le flag `hasPrefilled` reste
  // true pour la durée de vie du modal — toute évolution ultérieure du
  // candidat (changement de format, etc.) est ignorée. Reset = fermer/rouvrir.
  const [hasPrefilled, setHasPrefilled] = useState(false)
  const [lastSeenCandidateKey, setLastSeenCandidateKey] = useState<string | null>(null)
  const candidateKey = prefillCandidate
    ? `${prefillCandidate.source.type}:${prefillCandidate.cents}`
    : null
  if (candidateKey !== lastSeenCandidateKey) {
    setLastSeenCandidateKey(candidateKey)
    if (!hasPrefilled && prefillCandidate) {
      const currentPrice = Number(form.getValues('price_cents')) || 0
      const isDirty = !!form.formState.dirtyFields.price_cents
      if (currentPrice === 0 && !isDirty) {
        form.setValue('price_cents', prefillCandidate.cents, { shouldDirty: false })
        setPrefillSource(prefillCandidate.source)
        setHasPrefilled(true)
      }
    }
  }

  // Catalogue vide → empty state pointant vers /settings (après hooks).
  if (!isLoading && formats.length === 0) {
    return (
      <Modal isOpen onClose={onClose} title="Catalogue vide" size="form">
        <Modal.Body>
          <EmptyState
            title="Aucun format dans ton catalogue"
            description="Configure d'abord au moins un format (30×40, 50×70, …) dans la page Réglages avant de créer une variante."
            action={
              <Link to="/settings">
                <Button variant="primary" onClick={onClose}>
                  Ouvrir les Réglages
                </Button>
              </Link>
            }
          />
        </Modal.Body>
      </Modal>
    )
  }

  // Filtre formats : 2 contraintes cumulatives.
  //   1. Kind compatible avec le mode (digital → 'digital' ; physique →
  //      au moins un kind ≠ 'digital'). Multi-kind S2.5e — ex. 60×90
  //      print+digital apparaît dans les deux modes.
  //   2. Ratio compatible avec AU MOINS UN source de l'œuvre à ±tolérance
  //      (multi-source migration 015). Un portrait 60×90 (0.667) match si
  //      l'œuvre a un ratio source dans [~0.633, ~0.700]. Liste vide
  //      `workSourceRatios` (pas de master) → pas de filtre.
  const ratioCompatibleFormats = formats.filter((f) =>
    isFormatCompatibleWithWork(f, workSourceRatios, formatMatchingTolerance),
  )
  const filteredFormats = ratioCompatibleFormats.filter((f) =>
    editionMode === 'digital' ? f.kinds.includes('digital') : f.kinds.some((k) => k !== 'digital'),
  )
  // Distingue "aucun format compatible ratio" de "aucun format dans le mode
  // sélectionné" — l'empty state du bloc 02 affiche un message différent.
  const noCompatibleRatio =
    workSourceRatios.length > 0 && formats.length > 0 && ratioCompatibleFormats.length === 0

  const isDigital = editionMode === 'digital'
  // Tirage physique réalisé en atelier (limited numéroté ou original unique) :
  // seuls modes où support/encadrement, stock physique et colis d'expédition
  // ont un sens. Open = Gelato imprime+expédie (spec POD porte la matière) ;
  // digital = fichier livré (rien de tout cela).
  const isAtelierPhysical = editionMode === 'limited' || editionMode === 'original'
  // Refonte POD v6.1 : 1 variante = 1 format publiable. Le multi-format
  // (mono variante × N tailles digitales) a été abandonné — l'utilisatrice
  // crée N variantes distinctes via `<VariantsBulkCreateForm>` (PR-C).
  const allowMultiFormat = false

  // Au changement de mode, on reset les champs qui ne sont plus pertinents
  // dans le nouveau mode (sinon utilisatrice voit des sélections fantômes).
  // Politique stricte par edition_kind :
  //   - open     → spec REQUIRED, format MUST be NULL, support/framing/stock/colis NULL
  //   - limited  → format REQUIRED, spec MUST be NULL, atelier physique complet
  //   - original → format optionnel, spec MUST be NULL, atelier physique complet
  //   - digital  → ni format, ni support/framing, ni spec, ni stock, ni colis
  const handleModeChange = (next: EditionMode) => {
    setEditionMode(next)
    // Sync les champs RHF dérivés du mode UI — sinon la validation Zod
    // tourne sur des valeurs périmées (ex : mode UI='digital' mais
    // form.medium_kind='print' → superRefine exige printable_spec_id qui
    // n'existe pas pour digital, erreur invisible côté UI → bouton submit
    // silencieux).
    form.setValue('medium_kind', next === 'digital' ? 'digital' : 'print', {
      shouldDirty: true,
      shouldValidate: true,
    })
    form.setValue('edition_kind', next === 'digital' ? 'open' : next, {
      shouldDirty: true,
      shouldValidate: true,
    })
    const currentId = form.getValues('format_id')
    if (currentId) {
      const fmt = formats.find((x) => String(x.id) === String(currentId))
      const compatible =
        fmt &&
        isFormatCompatibleWithWork(fmt, workSourceRatios, formatMatchingTolerance) &&
        (next === 'digital'
          ? fmt.kinds.includes('digital')
          : fmt.kinds.some((k) => k !== 'digital'))
      if (!compatible) {
        form.setValue('format_id', '', { shouldDirty: true, shouldValidate: true })
      }
    }
    // open → drop format_id (la spec porte les tailles)
    // digital → drop format_id (refonte 2026-05-14 : N formats résolus
    //   dynamiquement côté BE, pas de format porté par la variante)
    if (next === 'open' || next === 'digital') {
      form.setValue('format_id', '', { shouldDirty: true, shouldValidate: true })
    }
    // limited / original → drop printable_spec_id (tirage atelier, pas POD)
    if (next === 'limited' || next === 'original') {
      form.setValue('printable_spec_id', '', { shouldDirty: true, shouldValidate: true })
    }
    // open / digital → l'atelier ne touche pas physiquement le tirage :
    // ni papier/cadre, ni stock physique, ni dimensions de colis.
    // Digital exige en plus de nettoyer la spec POD.
    if (next === 'open' || next === 'digital') {
      form.setValue('framing_id', '', { shouldDirty: true })
      form.setValue('support_id', '', { shouldDirty: true })
      form.setValue('stock_qty', undefined, { shouldDirty: true })
      form.setValue('weight_g', undefined, { shouldDirty: true })
      form.setValue('packaged_width_cm', undefined, { shouldDirty: true })
      form.setValue('packaged_height_cm', undefined, { shouldDirty: true })
      form.setValue('packaged_depth_cm', undefined, { shouldDirty: true })
    }
    if (next === 'digital') {
      form.setValue('printable_spec_id', '', { shouldDirty: true })
    }
  }

  // Sélection format — mono. Le wizard bulk (PR-C) gère le multi-tailles
  // en créant N variantes distinctes plutôt que N formats dans une variante.
  const handleFormatToggle = (formatId: string) => {
    form.setValue('format_id', formatId, { shouldDirty: true, shouldValidate: true })
  }

  const submitting = form.formState.isSubmitting

  // Preview live des livrables digitaux. Refonte POD v6.1 : 1 variante =
  // 1 format publiable → max 2 fichiers (JPG q100 + PDF) si le format
  // sélectionné porte le kind 'digital' ET status='active'.
  const selectedDigitalFormat = (() => {
    if (!watchedFormatId) return null
    const f = formats.find((x) => String(x.id) === String(watchedFormatId))
    if (!f) return null
    if (!f.kinds.includes('digital')) return null
    return f
  })()
  const willGenerateDeliverables = selectedDigitalFormat !== null && watchedStatus === 'active'
  const deliverableCount = selectedDigitalFormat ? 2 : 0 // JPG + PDF

  // Aperçu footer : « Open edition · A3 » (mono format).
  const previewModeLabel = isDigital ? 'Set numérique' : EDITION_KIND_FR[editionMode]
  const previewFormatLabel = (() => {
    if (!watchedFormatId) return null
    const f = formats.find((x) => String(x.id) === String(watchedFormatId))
    return f?.label_fr ?? null
  })()
  const previewLabel = previewFormatLabel
    ? `« ${previewModeLabel} · ${previewFormatLabel} »`
    : `« ${previewModeLabel} »`

  const handleValid = async (values: VariantFormParsed) => {
    // Refonte POD v6.1 : `medium_kind` est dérivé de l'editionMode UI.
    // - mode 'digital' → medium_kind='digital' + edition_kind='open' forcé
    //   (les livrables digitaux n'ont pas de notion d'édition limitée)
    // - mode 'original' / 'limited' / 'open' → medium_kind='print'
    //   (politique unique : un original physique est un 'print' au sens
    //   du canal de vente, son caractère unique vit dans `edition_kind`)
    //
    // PR-D V3 — politique stricte avant submit :
    //   - open : force format_id=undefined (la spec porte les tailles)
    //   - limited / original : force printable_spec_id=undefined
    // Le Zod superRefine valide déjà ces invariants mais on les normalise
    // ici aussi en cas de valeur fantôme post-handleModeChange.
    let payload: VariantFormParsed = isDigital
      ? { ...values, medium_kind: 'digital', edition_kind: 'open', format_id: undefined }
      : { ...values, medium_kind: 'print' }
    if (payload.edition_kind === 'open' && !isDigital) {
      payload = { ...payload, format_id: undefined }
    }
    if (payload.edition_kind === 'limited' || payload.edition_kind === 'original') {
      payload = { ...payload, printable_spec_id: undefined }
    }
    // Open POD et digital : pas d'atelier physique → support/encadrement,
    // stock physique et dimensions de colis n'existent pas. On nettoie ici
    // pour neutraliser d'éventuelles valeurs fantômes héritées d'un edit
    // antérieur (ou d'un mode bascule rapide) avant l'envoi au BE.
    if (!isAtelierPhysical) {
      payload = {
        ...payload,
        support_id: undefined,
        framing_id: undefined,
        stock_qty: undefined,
        weight_g: undefined,
        packaged_width_cm: undefined,
        packaged_height_cm: undefined,
        packaged_depth_cm: undefined,
      }
    }
    if (mode === 'create') {
      await new Promise<void>((resolve, reject) => {
        addMutation.mutate(
          { workId, payload },
          {
            onSuccess: () => {
              toast.success('Variante ajoutée')
              onClose()
              resolve()
            },
            onError: (err) => {
              toast.error(err.detail ?? 'Création impossible')
              reject(err)
            },
          },
        )
      })
    } else if (initial) {
      await new Promise<void>((resolve, reject) => {
        updateMutation.mutate(
          { workId, variantId: initial.id, payload },
          {
            onSuccess: () => {
              toast.success('Variante mise à jour')
              onClose()
              resolve()
            },
            onError: (err) => {
              toast.error(err.detail ?? 'Mise à jour impossible')
              reject(err)
            },
          },
        )
      })
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={mode === 'create' ? 'Nouvelle variante' : 'Modifier la variante'}
      size="large"
      kicker="Œuvre · Variantes"
    >
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleValid)} noValidate className="variant-modal-form">
          <Modal.Body className="variant-modal-body">
            {/* ── 01 Type d'édition ────────────────────────────────────── */}
            <FormSection number="01" title="Type d'édition">
              <div
                className="variant-modal-edition-grid"
                role="radiogroup"
                aria-label="Type d'édition"
              >
                {EDITION_MODE_CARDS.map((card) => {
                  const Icon = card.icon
                  return (
                    <RadioCard
                      key={card.value}
                      selected={editionMode === card.value}
                      onSelect={() => handleModeChange(card.value)}
                      icon={<Icon size={16} />}
                      label={card.label}
                      description={card.description}
                    />
                  )
                })}
              </div>
            </FormSection>

            {/* ── 02 Format (physique : limited / original) ───────────── */}
            {/* PR-D V3 — masqué pour `edition_kind='open'` (POD) : la
                spec POD porte les tailles, le client choisit au checkout
                Etsy. Refonte digital 2026-05-14 : aussi masqué en mode
                'digital' — les fichiers livrés sont résolus dynamiquement
                côté BE (catalogue actif kind='digital' ∩ ratios masters).
                Le bloc 02bis ci-dessous affiche la preview live des
                fichiers résolus. */}
            {editionMode !== 'open' && !isDigital && (
              <FormSection
                number="02"
                title="Format"
                sub={
                  editionMode === 'original'
                    ? 'Optionnel — dimensions de la pièce unique.'
                    : allowMultiFormat
                      ? 'Plusieurs tailles possibles — 1 livrable JPG + PDF généré par format.'
                      : undefined
                }
              >
                {filteredFormats.length === 0 ? (
                  <p className="variant-modal-empty-block">
                    {noCompatibleRatio ? (
                      <>
                        Aucun format compatible avec les ratios disponibles sur cette œuvre
                        {workSourceRatios.length === 1
                          ? ` (${workSourceRatios[0].toFixed(2)}, ±${(formatMatchingTolerance * 100).toFixed(0)} %)`
                          : ` (${workSourceRatios.length} ratios, ±${(formatMatchingTolerance * 100).toFixed(0)} %)`}
                        . Ajoute un master au ratio adapté ou un format adapté dans{' '}
                        <Link to="/settings">Réglages</Link>.
                      </>
                    ) : (
                      <>
                        Aucun format {isDigital ? 'digital' : 'physique'} compatible. Configure-le
                        dans <Link to="/settings">Réglages</Link>.
                      </>
                    )}
                  </p>
                ) : (
                  <Controller
                    control={form.control}
                    name="format_id"
                    render={({ fieldState }) => (
                      <>
                        <div className="variant-modal-format-grid">
                          {filteredFormats.map((f) => {
                            const fid = String(f.id)
                            const selected = String(watchedFormatId ?? '') === fid
                            // `label_fr` est dérivé service "60×90 cm (2:3)"
                            // depuis S2.5e — il porte déjà dimensions + ratio.
                            return (
                              <RadioCard
                                key={f.id}
                                selected={selected}
                                onSelect={() => handleFormatToggle(fid)}
                                label={f.label_fr}
                                multi={allowMultiFormat}
                              />
                            )
                          })}
                        </div>
                        {fieldState.error?.message && (
                          <p className="variant-modal-error">{fieldState.error.message}</p>
                        )}
                      </>
                    )}
                  />
                )}

                {willGenerateDeliverables && selectedDigitalFormat && (
                  <p className="variant-modal-deliverable-preview" role="status" aria-live="polite">
                    <strong>{deliverableCount} fichiers</strong> seront générés à 300 DPI&nbsp;: JPG
                    q100 + PDF. Le master doit être ≥{' '}
                    {Math.round(
                      (Math.min(
                        Number(selectedDigitalFormat.width_cm),
                        Number(selectedDigitalFormat.height_cm),
                      ) *
                        300) /
                        2.54,
                    )}
                    &nbsp;px côté court — sinon la création sera bloquée.
                  </p>
                )}
              </FormSection>
            )}

            {/* ── 02 Fichiers livrés (mode digital — refonte 2026-05-14) ─ */}
            {/* La variante digitale ne choisit plus de format : le BE
                résout dynamiquement l'intersection des formats catalogue
                actifs kind='digital' avec les ratios des masters de
                l'œuvre. Cette section liste ce qui sera livré. */}
            {isDigital && <DigitalDeliverablesPreviewSection workId={workId} />}

            {/* ── 03 Support & encadrement (visible uniquement pour tirage
                atelier physique — limited/original. En open, la spec POD
                porte la matière ; en digital, fichier sans papier/cadre). */}
            {isAtelierPhysical && (
              <FormSection number="03" title="Support & encadrement">
                <div className="variant-modal-chip-cols">
                  <div className="variant-modal-chip-group">
                    <span className="variant-modal-chip-label">Papier / support</span>
                    <Controller
                      control={form.control}
                      name="support_id"
                      render={({ field }) => (
                        <div className="variant-modal-chip-row" role="radiogroup">
                          <ChipToggle active={!field.value} onClick={() => field.onChange('')}>
                            Aucun
                          </ChipToggle>
                          {supports.map((s) => (
                            <ChipToggle
                              key={s.id}
                              active={String(field.value) === String(s.id)}
                              onClick={() => field.onChange(String(s.id))}
                            >
                              {s.label_fr}
                            </ChipToggle>
                          ))}
                        </div>
                      )}
                    />
                  </div>

                  <div className="variant-modal-chip-group">
                    <span className="variant-modal-chip-label">Encadrement</span>
                    <Controller
                      control={form.control}
                      name="framing_id"
                      render={({ field }) => (
                        <div className="variant-modal-chip-row" role="radiogroup">
                          <ChipToggle active={!field.value} onClick={() => field.onChange('')}>
                            Sans encadrement
                          </ChipToggle>
                          {framings.map((f) => (
                            <ChipToggle
                              key={f.id}
                              active={String(field.value) === String(f.id)}
                              onClick={() => field.onChange(String(f.id))}
                            >
                              {f.label_fr}
                            </ChipToggle>
                          ))}
                        </div>
                      )}
                    />
                  </div>
                </div>
              </FormSection>
            )}

            {/* ── 04 Tarif & disponibilité ─────────────────────────────── */}
            <FormSection
              number="04"
              title={
                editionMode === 'open' ? 'Production & disponibilité' : 'Tarif & disponibilité'
              }
            >
              {/* Refonte tarifs 2026-05-15 (option B) — POD : le prix est
                  dérivé au publish via `pricing_service.compute_pod_retail_price_cents`
                  (base_cost Gelato + marge atelier + frais Etsy). On cache
                  le champ Prix pour éviter toute saisie qui serait ignorée
                  au publish (incohérence UX). `price_cents` reste à 0 dans
                  le payload — accepté côté BE pour les variantes POD. */}
              {editionMode === 'open' ? (
                <p className="variant-modal-prefill-hint ui-body-sm ui-text-muted" role="status">
                  Variante POD — le prix final affiché sur Etsy sera calculé au publish à partir du
                  coût Gelato + marge atelier + frais Etsy (cf. <em>Réglages → Tarification</em>).
                  Pas de saisie ici.
                </p>
              ) : (
                <div
                  className={`variant-modal-tarif-row${isAtelierPhysical ? '' : ' variant-modal-tarif-row--price-only'}`}
                >
                  <Controller
                    control={form.control}
                    name="price_cents"
                    render={({ field, fieldState }) => (
                      <Field
                        label="Prix net atelier"
                        required
                        error={fieldState.error?.message}
                        helper={(() => {
                          // Helper dynamique aligné avec la valeur réelle des
                          // frais Etsy configurés (Settings → Tarification).
                          // Évite la divergence helper hardcodé vs preview Etsy
                          // live qui peut perturber l'utilisatrice quand
                          // `etsy_fees_pct` est ajusté.
                          const fees = podPricingConfigQuery.data?.etsy_fees_pct
                          const feesStr =
                            fees != null && fees > 0
                              ? `${fees % 1 === 0 ? fees.toFixed(0) : fees.toFixed(1)} %`
                              : null
                          return feesStr
                            ? `Ce que tu touches après prélèvement Etsy. Le prix affiché à l'acheteur est ajusté automatiquement pour compenser les frais (${feesStr}).`
                            : "Ce que tu touches après prélèvement Etsy. Le prix affiché à l'acheteur est ajusté automatiquement pour compenser les frais."
                        })()}
                      >
                        {(controlProps) => (
                          <div className="variant-modal-price-input">
                            <Input
                              {...controlProps}
                              type="number"
                              step="0.01"
                              min={0}
                              value={(() => {
                                const raw = field.value
                                const cents = typeof raw === 'number' ? raw : raw ? Number(raw) : 0
                                return cents > 0 ? String(cents / 100) : ''
                              })()}
                              onChange={(e) => {
                                const raw = e.target.value
                                if (raw === '') {
                                  field.onChange(0)
                                  return
                                }
                                const euros = parseFloat(raw)
                                field.onChange(Number.isNaN(euros) ? 0 : Math.round(euros * 100))
                              }}
                              onBlur={field.onBlur}
                              name={field.name}
                              ref={field.ref}
                            />
                            <span className="variant-modal-price-suffix" aria-hidden="true">
                              €
                            </span>
                          </div>
                        )}
                      </Field>
                    )}
                  />

                  {isAtelierPhysical && (
                    <FormField
                      name="stock_qty"
                      label="Stock"
                      type="number"
                      min={0}
                      helper="Vide = impression à la commande."
                    />
                  )}
                </div>
              )}

              {editionMode !== 'open' &&
                prefillSource &&
                !form.formState.dirtyFields.price_cents && (
                  <p
                    className="variant-modal-prefill-hint ui-body-sm ui-text-muted"
                    role="status"
                    aria-live="polite"
                  >
                    {prefillSourceLabel(prefillSource)}
                  </p>
                )}

              {/* Preview "Prix affiché Etsy" — option B (refonte tarifs 2026-05-15) :
                  pour digital/original/limited, la saisie représente le NET atelier
                  souhaité. Le prix réellement affiché à l'acheteur Etsy compense
                  les frais via `pricing_service.apply_etsy_fees_compensation`
                  côté BE au publish. Cette preview reproduit la formule en JS pur
                  pour transparence. POD ('open') est géré au-dessus (hint
                  informatif sans champ saisi).
               */}
              {editionMode !== 'open' &&
                (() => {
                  const watchedPrice = Number(form.watch('price_cents')) || 0
                  const fees = podPricingConfigQuery.data?.etsy_fees_pct ?? 0
                  if (watchedPrice <= 0 || fees < 0 || fees >= 100) return null
                  // Reproduction exacte de `round_up_to_euro_cents` BE :
                  // ((ceil(net / (1 - fees/100)) + 99) // 100) * 100
                  const raw = watchedPrice / (1 - fees / 100)
                  const rawInt = Math.ceil(raw)
                  const listedCents = Math.ceil(rawInt / 100) * 100
                  if (listedCents <= watchedPrice) return null
                  const listedEuros = (listedCents / 100).toFixed(2).replace('.', ',')
                  return (
                    <p
                      className="variant-modal-prefill-hint ui-body-sm ui-text-muted"
                      role="status"
                      aria-live="polite"
                    >
                      Prix affiché Etsy : <strong>{listedEuros} €</strong> (compensation des frais
                      Etsy {fees} % — tu touches pile ton net saisi).
                    </p>
                  )
                })()}

              {/* Option B 2026-05-18 — POD géré par la matrice bi-axe
                  (Size × Color), auto-créée à la création de variante via
                  les défauts atelier (`pod_matrix_default_spec_by_finish`).
                  Plus de picker spec ici — la matrice route vers la bonne
                  spec par finition au publish. Configuration centralisée
                  dans Réglages → Catalogue POD. */}
              {editionMode === 'open' && (
                <div className="variant-modal-pod-matrix-note ui-body-sm ui-text-muted">
                  Production POD gérée par la matrice bi-axe (Size × Color), configurée dans{' '}
                  <Link to="/settings?tab=printable-specs">Réglages → Catalogue POD</Link>. La
                  matrice s'active automatiquement à la création.
                </div>
              )}

              {editionMode === 'limited' && (
                <FormField
                  name="edition_size"
                  label="Taille édition"
                  type="number"
                  min={1}
                  helper="Nombre total de tirages numérotés."
                />
              )}

              <FormField
                name="label_override"
                label="Nom commercial"
                helper="Vide → libellé dérivé automatiquement."
              />

              <div className="variant-modal-chip-group">
                <span className="variant-modal-chip-label">État</span>
                <Controller
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <div className="variant-modal-chip-row" role="radiogroup">
                      <ChipToggle
                        active={field.value === 'draft'}
                        onClick={() => field.onChange('draft')}
                      >
                        Brouillon
                      </ChipToggle>
                      <ChipToggle
                        active={field.value === 'active'}
                        onClick={() => field.onChange('active')}
                      >
                        En boutique
                      </ChipToggle>
                      <ChipToggle
                        active={field.value === 'archived'}
                        onClick={() => field.onChange('archived')}
                      >
                        Archivée
                      </ChipToggle>
                    </div>
                  )}
                />
              </div>
            </FormSection>

            {/* ── 05 Expédition (visible uniquement pour tirage atelier
                physique — limited/original. Open = Gelato packagise et
                expédie depuis son lab ; digital = pas de colis). */}
            {isAtelierPhysical && (
              <FormSection
                number="05"
                title="Expédition"
                actions={
                  <Button
                    type="button"
                    variant="quiet"
                    size="sm"
                    onClick={() => setShowShipping((s) => !s)}
                  >
                    {showShipping ? '— Masquer' : '+ Afficher'}
                  </Button>
                }
              >
                {showShipping && (
                  <div className="variant-modal-shipping-row">
                    <FormField name="weight_g" label="Poids (g)" type="number" min={0} />
                    <FormField
                      name="packaged_width_cm"
                      label="Colis L (cm)"
                      type="number"
                      min={0}
                    />
                    <FormField
                      name="packaged_height_cm"
                      label="Colis H (cm)"
                      type="number"
                      min={0}
                    />
                    <FormField
                      name="packaged_depth_cm"
                      label="Colis P (cm)"
                      type="number"
                      min={0}
                    />
                  </div>
                )}
              </FormSection>
            )}
          </Modal.Body>

          <Modal.Footer
            leftActions={
              <span className="variant-modal-footer-preview">
                <span className="variant-modal-footer-preview-label">Aperçu</span>
                <span className="variant-modal-footer-preview-value">{previewLabel}</span>
              </span>
            }
          >
            {onBack && (
              <Button variant="quiet" type="button" onClick={onBack} disabled={submitting}>
                ← Changer de type
              </Button>
            )}
            <Button variant="ghost" type="button" onClick={onClose} disabled={submitting}>
              Annuler
            </Button>
            <Button variant="primary" type="submit" loading={submitting}>
              {mode === 'create' ? 'Ajouter la variante →' : 'Enregistrer'}
            </Button>
          </Modal.Footer>
        </form>
      </FormProvider>
    </Modal>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// Sous-composants
// ──────────────────────────────────────────────────────────────────────────

interface ChipToggleProps {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}

/** Chip toggle single-select — sélectionné = neutral (encre), sinon ghost. */
function ChipToggle({ active, onClick, children }: ChipToggleProps) {
  return (
    <Button
      type="button"
      variant={active ? 'neutral' : 'ghost'}
      size="sm"
      onClick={onClick}
      aria-pressed={active}
    >
      {children}
    </Button>
  )
}

/** Section 02 affichée en mode `digital` — refonte 2026-05-14 : la variante
 *  digitale ne porte plus de format_id. Le BE résout dynamiquement les
 *  fichiers livrés via l'intersection (catalogue actif kind='digital' ∩
 *  ratios masters de l'œuvre). On affiche la liste résolue + le total. */
function DigitalDeliverablesPreviewSection({ workId }: { workId: number }) {
  const previewQuery = useDigitalFormatsPreview(workId)

  return (
    <FormSection
      number="02"
      title="Fichiers livrés (automatique)"
      sub="Tous les formats catalogue actifs kind='digital' compatibles avec un master de l'œuvre sont inclus dans le ZIP. Pas de choix : ajoute des formats dans /settings ou un master au ratio adapté pour enrichir le kit."
    >
      {previewQuery.isLoading && (
        <p className="ui-body-sm ui-text-muted" role="status" aria-live="polite">
          Calcul des fichiers livrés…
        </p>
      )}
      {previewQuery.isError && (
        <p className="variant-modal-empty-block" role="alert">
          Impossible de calculer la preview des fichiers livrés. Réessaie dans un instant.
        </p>
      )}
      {previewQuery.data && previewQuery.data.formats.length === 0 && (
        <p className="variant-modal-empty-block">
          Aucun format catalogue digital compatible avec les masters de cette œuvre. Active des
          formats <code>kind=&quot;digital&quot;</code> dans <Link to="/settings">Réglages</Link> OU
          uploade un master au ratio adapté.
        </p>
      )}
      {previewQuery.data && previewQuery.data.formats.length > 0 && (
        <div className="variant-modal-deliverable-preview" role="status" aria-live="polite">
          <p>
            <strong>{previewQuery.data.total_files} fichiers</strong> seront générés à{' '}
            {previewQuery.data.dpi} DPI&nbsp;:
          </p>
          <ul className="variant-modal-digital-formats-list">
            {previewQuery.data.formats.map((f) => (
              <li key={f.format_id}>{f.label_fr}</li>
            ))}
          </ul>
        </div>
      )}
    </FormSection>
  )
}
